import { defineMessages } from "@uzverse/shared";

/** All UI strings of the web client (namespace "web"). Turkish must mirror English. */
export const webMessages = defineMessages("web", {
  en: {
    skipToContent: "Skip to content",
    nav: {
      label: "Main navigation",
      assistant: "Assistant",
      world: "World",
      companies: "Companies",
      more: "More",
      tasks: "Tasks",
      approvals: "Approvals",
      memory: "Memory",
      credits: "Credits",
      settings: "Settings",
      signOut: "Sign out",
      collapse: "Collapse sidebar",
      expand: "Expand sidebar",
      showContext: "Show context panel",
      hideContext: "Hide context panel",
      renameAssistant: "Rename your assistant",
    },
    shell: {
      assistantName: "Assistant name",
      save: "Save",
      cancel: "Cancel",
      signedInAs: "Signed in as {{name}}",
    },
    login: {
      title: "Sign in",
      email: "Email",
      password: "Password",
      continue: "Continue",
      totpTitle: "Two-step verification",
      totpHint: "Enter the 6-digit code from your authenticator app.",
      code: "Code",
      verify: "Verify",
      working: "Working…",
      remember: "Remember me",
      trustDevice: "Trust this device for 30 days",
      sessionEnded: "Your session ended. Please sign in again.",
      inviteHint: "Uzverse is invite-only. Open your invite link to create an account.",
    },
    invite: {
      title: "Accept your invite",
      intro: "{{name}} invited {{email}} to Uzverse.",
      displayName: "Your name",
      password: "Choose a password",
      passwordHint: "At least 12 characters.",
      disclosure: "I understand that the owner of this Uzverse can see everything I do here.",
      accept: "Create account",
      enrollTitle: "Set up your authenticator",
      enrollHint:
        "Scan this link with an authenticator app, or type the key by hand. Then enter the 6-digit code.",
      manualKey: "Setup key",
      openLink: "Open in authenticator app",
      confirm: "Confirm and sign in",
      invalid: "This invite is not valid or has expired.",
    },
    chat: {
      title: "Your assistant",
      you: "You",
      assistant: "Assistant",
      placeholder: "Ask your assistant to do something…",
      messageLabel: "Message",
      send: "Send",
      speakReplies: "Read replies aloud",
      empty: "Say what you need. Your assistant will take it from there.",
      log: "Conversation",
      streaming: "Writing…",
      connection: {
        connecting: "Connecting…",
        online: "Connected",
        offline: "Offline. Reconnecting…",
      },
      artifact: "Open {{name}}",
    },
    task: {
      state: {
        received: "Received",
        routing: "Finding the right team",
        planning: "Planning",
        in_progress: "In progress",
        review: "Being reviewed",
        awaiting_approval: "Waiting for your approval",
        done: "Done",
        needs_owner: "Needs the owner",
        cancelled: "Cancelled",
      },
      progress: "Progress of: {{text}}",
      untitled: "Task",
    },
    tasks: {
      title: "Tasks",
      empty: "No tasks yet. Ask your assistant in the chat.",
      active: "Active",
      finished: "Finished",
      artifacts: "Files",
    },
    approvals: {
      title: "Approvals",
      intro: "Your assistant asks before it does anything outside Uzverse.",
      empty: "Nothing is waiting for you.",
      pending: "Waiting for you",
      resolved: "Decided",
      wants: "Wants to run {{tool}}",
      input: "Details",
      effect: {
        none: "No outside effect",
        internal: "Inside Uzverse",
        intercompany: "Message to another company",
        external: "Outside message or post",
        spend: "Spends money",
      },
      status: {
        pending: "Pending",
        approved: "Approved",
        rejected: "Rejected",
        expired: "Expired",
      },
    },
    avatar: {
      title: "My character",
      intro: "This is you in the 3D world. Your look follows you across devices.",
      body: "Body",
      male: "Male",
      female: "Female",
      style: "Style",
      styles: { office: "Office person", classic: "Classic" },
      person: "Person",
      look: { business: "Business {{n}}", office: "Office {{n}}" },
      skin: "Skin tone",
      hair: "Hairstyle",
      hairColor: "Hair color",
      outfit: "Outfit",
      outfitColor: "Outfit color",
      surprise: "Surprise me",
      save: "Save my character",
      saved: "Character saved.",
      note: "Characters are drawn from the bundled realistic models (the office people are Microsoft Rocketbox, MIT); nothing is fetched from other sites.",
      assistantLook: "Assistant look",
      assistantNote:
        "How your assistant looks in the world and on its page. Saved with the assistant settings.",
      portrait: "{{name}}, portrait",
      hairStyle: {
        buzzed: "Buzz cut",
        parted: "Side part",
        long: "Long",
        buns: "Buns",
        "short-f": "Short",
        beard: "Beard",
      },
      outfits: {
        tee: "T-shirt",
        shirt: "Shirt",
        blazer: "Blazer",
        shorts: "Shorts",
        hoodie: "Hoodie",
      },
    },
    mh: {
      styleLabel: "Character type",
      styleMh: "MakeHuman (recommended)",
      styleOther: "Office person / classic",
      title: "My character",
      sex: "Sex",
      sexes: {
        male: "Man",
        female: "Woman",
      },
      presets: "Presets",
      undo: "Undo",
      redo: "Redo",
      randomAll: "Surprise me",
      randomize: "Randomise",
      reset: "Reset",
      save: "Save my character",
      saved: "Character saved.",
      viewLabel: "{{name}}, 3D character",
      loading: "Loading the character…",
      no3d: "3D view needs WebGL.",
      focus: {
        face: "Face",
        body: "Body",
      },
      tabs: {
        body: "Body",
        face: "Face",
        skin: "Skin",
        hair: "Hair",
        beard: "Beard",
        outfit: "Outfit",
        accessory: "Accessories",
      },
      skinTone: "Skin tone",
      eyeColor: "Eye color",
      brows: "Eyebrows",
      hairStyle: "Hairstyle",
      noHair: "Bald",
      hairColor: "Hair color",
      customColor: "Custom color",
      beard: "Beard",
      noBeard: "Clean-shaven",
      beardMale: "Beards are for male characters.",
      color: "Color",
      slots: {
        top: "Top",
        bottom: "Bottom",
        shoes: "Shoes",
      },
      styles: {
        business: "Business",
        smart: "Business casual",
        casual: "Casual",
        sport: "Sporty",
      },
      acc: {
        glasses: "Glasses",
        hat: "Hats",
      },
      intro:
        "This is you in the 3D world: shape the body and face, pick the skin, hair and clothes. Your character follows you across devices.",
      note: "Made from MakeHuman (CC0) assets bundled with Uzverse; nothing is fetched from other sites.",
      assistantNote:
        "How your assistant looks in the world and on its page. Saved with the assistant settings.",
      sliders: {
        height: "Height",
        weight: "Weight",
        muscle: "Muscle",
        age: "Age",
        proportions: "Proportions",
        breast: "Chest / bust",
        shoulders: "Shoulder width",
        chest: "Chest size",
        waist: "Waist",
        hips: "Hips",
        belly: "Belly",
        buttocks: "Buttocks",
        neckLength: "Neck length",
        neckWidth: "Neck thickness",
        armLength: "Arm length",
        legLength: "Leg length",
        headSize: "Head size",
        faceFullness: "Face fullness",
        faceSquare: "Round ↔ square",
        faceTaper: "Heart ↔ pear",
        faceLength: "Diamond ↔ long",
        jawWidth: "Jaw width",
        chinWidth: "Chin width",
        chinHeight: "Chin height",
        chinProtrusion: "Chin projection",
        cheekbones: "Cheekbones",
        cheekVolume: "Cheeks",
        forehead: "Forehead height",
        noseSize: "Nose size",
        noseWidth: "Nose width",
        noseLength: "Nose length",
        noseBridge: "Nose bridge (hump)",
        noseTip: "Nose tip",
        nostrils: "Nostrils",
        eyeSize: "Eye size",
        eyeSpacing: "Eye spacing",
        eyeTilt: "Eye tilt",
        eyeOpening: "Eye opening",
        browHeight: "Brow height",
        browAngle: "Brow angle",
        lipFullness: "Lip fullness",
        mouthWidth: "Mouth width",
        mouthHeight: "Mouth position",
        mouthCorners: "Mouth corners",
        earSize: "Ear size",
        earProtrusion: "Ear protrusion",
      },
      items: {
        eyes: "Eyes",
        "lashes-m": "Lashes",
        "lashes-f": "Lashes",
        "brows-natural": "Natural",
        "brows-arched": "Arched",
        "brows-soft": "Soft",
        "brows-thin": "Thin",
        "brows-bold": "Bold",
        "brows-straight": "Straight",
        "hair-short-crop": "Short crop",
        "hair-short-side": "Side part",
        "hair-short-messy": "Messy",
        "hair-pixie": "Pixie",
        "hair-afro": "Afro",
        "hair-bob": "Bob",
        "hair-bob-long": "Long bob",
        "hair-long": "Long",
        "hair-ponytail": "Ponytail",
        "hair-braid": "Braid",
        "beard-stubble": "Stubble",
        "beard-moustache": "Moustache",
        "beard-full": "Full beard",
        "beard-long": "Long beard",
        "beard-goatee": "Goatee",
        "top-suit-tie": "Suit with tie",
        "top-suit-3": "Three-piece",
        "top-suit-classic": "Classic suit",
        "top-suit-double": "Double-breasted",
        "top-shirt-striped": "Striped shirt",
        "top-jacket-casual": "Casual jacket",
        "top-sweater": "Knit sweater",
        "top-tee": "T-shirt",
        "top-polo": "Polo",
        "top-suit-f": "Suit jacket",
        "top-suit-f2": "Tailored jacket",
        "top-suit-f-double": "Double-breasted",
        "top-blouse": "Blouse",
        "top-turtleneck": "Turtleneck",
        "top-tee-f": "T-shirt",
        "top-sport-f": "Sports top",
        "bottom-suit": "Suit trousers",
        "bottom-suit-classic": "Classic trousers",
        "bottom-chinos": "Chinos",
        "bottom-jeans": "Jeans",
        "bottom-cargo": "Cargo",
        "bottom-shorts": "Shorts",
        "bottom-suit-f": "Suit skirt",
        "bottom-suit-f2": "Suit trousers",
        "bottom-skirt": "Pencil skirt",
        "bottom-jeans-f": "Jeans",
        "bottom-leggings": "Leggings",
        "shoes-oxford": "Oxfords",
        "shoes-loafer": "Loafers",
        "shoes-boots": "Ankle boots",
        "shoes-sneaker-white": "White sneakers",
        "shoes-sneaker-blue": "Running shoes",
        "shoes-flats": "Flats",
        "shoes-booties": "Heeled booties",
        "glasses-frames": "Frames",
        "glasses-round": "Round",
        "glasses-square": "Square",
        "hat-fedora": "Fedora",
        "hat-newsboy": "Newsboy cap",
      },
      preset: {
        executive: "Executive",
        creative: "Creative",
        athlete: "Athlete",
        scholar: "Scholar",
        casual: "Casual",
        mature: "Mature",
        young: "Young",
        bearded: "Bearded",
        elegant: "Elegant",
      },
    },
    settings: {
      title: "Settings",
      saved: "Saved.",
      language: "Language",
      assistant: {
        title: "Your assistant",
        name: "Assistant name",
        appearance: "Appearance",
        appearanceDefault: "Default",
        appearanceNote: "More looks arrive with the 3D world.",
        proactivity: "How proactive should it be?",
        proactivityLevel: {
          off: "Off",
          low: "Low",
          medium: "Medium",
          high: "High",
        },
        proactivityHint: {
          off: "Only acts when you ask.",
          low: "Suggests rarely, only when it matters.",
          medium: "Suggests useful next steps.",
          high: "Takes initiative on routine things, always asking before anything outward.",
        },
      },
      voice: {
        title: "Voice",
        select: "Voice",
        none: "No voice (text only)",
        free: "Free",
        paid: "Paid",
        preview: "Preview voice",
        previewing: "Playing sample…",
        noVoices: "No voices are available for your language yet.",
        by: "from {{provider}}",
      },
      sessions: {
        title: "Sessions",
        intro: "Browsers and devices signed in to your account. Sign out any you do not recognise.",
        current: "This device",
        lastSeen: "Last active {{when}}",
        signedInAt: "Signed in {{when}}",
        remembered: "Stays signed in for 30 days of use",
        notRemembered: "Signs out after 12 idle hours",
        unknownDevice: "Unknown device",
        revoke: "Sign out",
        revoked: "Session signed out.",
        devicesTitle: "Trusted devices",
        devicesIntro: "These browsers skip the verification code until the date shown.",
        noDevices: "No trusted devices.",
        trustedUntil: "Trusted until {{when}}",
        removeDevice: "Remove",
        deviceRemoved: "Device removed.",
      },
      security: {
        title: "Security",
        intro:
          "Two-step verification protects your account. Changing it needs your password and a current code.",
        password: "Current password",
        code: "Current code",
        continue: "Continue",
        recoveryAction: "Regenerate recovery codes",
        recoveryHint: "Creates 10 new codes. The old ones stop working.",
        reenrollAction: "Set up a new authenticator",
        reenrollHint: "Replaces your current authenticator and signs out your other devices.",
        recoveryDone: "New recovery codes created.",
        reenrolled: "Your new authenticator is active.",
        ownerNote:
          "Your two-step verification is managed from the server command line (reset-owner-mfa) and finished in the owner panel.",
      },
      advancedLink: "Advanced settings",
    },
    advanced: {
      title: "Advanced settings",
      intro: "These come from the modules you use. The defaults are fine for most people.",
      empty: "No advanced settings are available.",
      reset: "Reset to default",
      on: "On",
      off: "Off",
      default: "Default: {{value}}",
    },
    credits: {
      title: "Credits",
      intro:
        "The 3D models and textures in the world come from free libraries. These are the creators and licenses of every approved asset.",
      empty: "No third-party assets are used in the world yet.",
      unavailable: "The credits could not be loaded.",
      count_one: "{{count}} asset",
      count_other: "{{count}} assets",
      by: "by {{author}}",
      unknownAuthor: "Author not stated",
      license: "License",
      source: "Source",
      noAttribution: "No attribution required.",
      licenseLink: "Read the {{license}} license",
      builtinTitle: "Built into the world",
      builtinIntro:
        "These free (CC0) packs ship with the world: furniture, plants and surfaces. No attribution is required; the creators are credited anyway.",
    },
    memory: {
      title: "What I know about you",
      intro: "Your assistant remembers these things to help you better. You are in control.",
      empty: "Nothing yet. Your assistant learns as you work together.",
      kind: { fact: "Fact", preference: "Preference", routine: "Routine" },
      edit: "Correct",
      editLabel: "Corrected text",
      saveCorrection: "Save correction",
      delete: "Delete",
      confirmDelete: "Delete this permanently? It cannot be recovered.",
      confirmYes: "Yes, delete",
      deleted: "Deleted.",
    },
    world: {
      title: "World",
    },
    voice: {
      recording: "Recording",
      playing: "Speaking",
      talk: "Hold to talk",
      listening: "Listening… release to send",
      transcribing: "Transcribing…",
      speaking: "Speaking…",
      stopSpeaking: "Stop speaking",
      unavailable: "Voice is not available in this browser.",
      micDenied: "Microphone access was denied.",
      failed: "Voice input failed. Please try again.",
      browserMode: "Using your browser's speech service",
    },
    paused: {
      title: "Paused: no AI connection",
      body: "No AI account is connected for this company, so I cannot work on this yet. It continues by itself as soon as one is connected.",
      bodyPublicBlocked:
        "A public AI connection is available, but this company is set to use only its own connections, so I cannot work on this yet.",
      ownerAction: "Connect an account",
      ownerActionPolicy: "Change the connection policy",
      ownerHint: "Opens the owner panel on this company's Connections page.",
      memberNotified: "The owner has been notified.",
    },
    chatUi: {
      hello: "What shall we build, {{name}}?",
      helloSub:
        "Tell your assistant what you need. It plans, delegates to your teams and reports back.",
      suggestTitle: "Try asking",
      suggest: {
        p1: {
          title: "Set up a company",
          prompt: "Set up the company Uzmoon and make the first floor software.",
        },
        p2: {
          title: "Plan my week",
          prompt: "Plan my week and tell me what needs my attention first.",
        },
        p3: { title: "Build a landing page", prompt: "Build a landing page for my café." },
        p4: { title: "Daily summary", prompt: "Give me a summary of what my companies did today." },
      },
      steps: "Steps",
      stepsShow: "Show steps",
      stepsHide: "Hide steps",
      stepsLabel: "What your assistant is doing",
      cost: "Cost so far",
      costUnknown: "not reported yet",
      attach: "Attach a file",
      attachSoon: "Attachments are coming soon",
      hint: "Enter to send, Shift+Enter for a new line",
      focusHint: "Press / to write",
      readAloudOn: "Replies are read aloud",
      readAloudOff: "Replies are not read aloud",
      modelUsed: "Model",
      effortUsed: "Effort",
    },
    context: {
      title: "Context",
      company: "Company",
      building: "Building",
      floor: "Floor",
      none: "No company yet. Ask your assistant to set one up.",
      assistant: "Assistant",
      appearance: "Appearance",
      voice: "Voice",
      noVoice: "No voice selected",
      budget: "Budget",
      budgetOf: "{{used}} of {{limit}}",
      budgetOpen: "{{used}} spent, no limit",
      budgetNone: "No budget data yet.",
      allSettings: "All preferences",
      saved: "Saved",
    },
    modelPick: {
      title: "Assistant model",
      intro:
        "Pick how your assistant thinks. Your company's policy sets the limits; Automatic lets the assistant decide.",
      model: "Model",
      effort: "Reasoning effort",
      auto: "Automatic",
      autoHint: "The assistant picks the model that suits each request.",
      tier: { fast: "Fast", standard: "Standard", strong: "Strong" },
      tierHint: {
        fast: "Fastest and cheapest. Quick answers and simple tasks.",
        standard: "Balanced. Good for most everyday work.",
        strong: "Strongest and most expensive. Code, analysis and hard problems.",
      },
      effortAutoHint: "The model decides how much to think.",
      effortLevel: {
        low: { name: "Low", hint: "Fast and cheap. Quick answers and simple tasks." },
        medium: { name: "Medium", hint: "Balanced speed and depth for everyday work." },
        high: { name: "High", hint: "Thinks longer. Better for code, analysis and planning." },
        xhigh: { name: "Very high", hint: "Extra depth for hard problems. Slower, costs more." },
        max: { name: "Maximum", hint: "Deepest reasoning. Slowest and most expensive." },
      },
      locked: "Your company's policy fixes the assistant's model.",
      benchmark: "Tip from your company",
      pillAuto: "Auto",
      pillLabel: "Model and effort",
      pillTitle: "Choose a model and effort for this message only",
      pillNote: "Applies to the next message only, within your company's limits.",
    },
    companies: {
      title: "Companies",
      intro: "Your companies, their buildings, floors and the agents working in them.",
      empty: "No companies yet",
      emptyHint:
        "Ask your assistant to set one up. It creates the building and hires the floor lead.",
      setup: "Ask your assistant",
      setupPrompt: "Set up the company Uzmoon and make the first floor software.",
      unavailable: "The company overview is not connected yet.",
      buildings_one: "{{count}} building",
      buildings_other: "{{count}} buildings",
      floors_one: "{{count}} floor",
      floors_other: "{{count}} floors",
      agents_one: "{{count}} agent",
      agents_other: "{{count}} agents",
      noFloors: "No floors yet",
      noAgents: "No agents yet",
      lead: "Lead",
      worker: "Worker",
      status: { idle: "Idle", working: "Working", offline: "Offline" },
      budget: "Budget",
      select: "Select a company",
    },
    onboarding: {
      title: "Meet your assistant",
      intro: "Give it a name and a voice. You can change both any time in Settings.",
      step: "Step {{n}} of {{total}}",
      nameTitle: "What should it be called?",
      nameHint: "Pick something you like to say out loud.",
      voiceTitle: "Choose a voice",
      voiceHint: "Preview a few. Free voices run on the server or in your browser.",
      next: "Continue",
      back: "Back",
      finish: "Start chatting",
      skip: "Skip for now",
      noVoices: "No voices are available for your language yet. You can pick one later.",
    },
    notFound: "This page does not exist.",
    connection: {
      reconnecting: "Reconnecting…",
      failed: "Could not reach the server.",
      retry: "Retry",
    },
  },
  tr: {
    skipToContent: "İçeriğe geç",
    nav: {
      label: "Ana gezinme",
      assistant: "Asistan",
      world: "Dünya",
      companies: "Şirketler",
      more: "Diğer",
      tasks: "Görevler",
      approvals: "Onaylar",
      memory: "Hafıza",
      credits: "Katkılar",
      settings: "Ayarlar",
      signOut: "Çıkış yap",
      collapse: "Kenar çubuğunu daralt",
      expand: "Kenar çubuğunu genişlet",
      showContext: "Bağlam panelini göster",
      hideContext: "Bağlam panelini gizle",
      renameAssistant: "Asistanınızı yeniden adlandırın",
    },
    shell: {
      assistantName: "Asistan adı",
      save: "Kaydet",
      cancel: "Vazgeç",
      signedInAs: "{{name}} olarak giriş yapıldı",
    },
    login: {
      title: "Giriş yap",
      email: "E-posta",
      password: "Parola",
      continue: "Devam",
      totpTitle: "İki adımlı doğrulama",
      totpHint: "Doğrulama uygulamanızdaki 6 haneli kodu girin.",
      code: "Kod",
      verify: "Doğrula",
      working: "İşleniyor…",
      remember: "Beni hatırla",
      trustDevice: "Bu cihazı 30 gün hatırla",
      sessionEnded: "Oturumunuz sona erdi. Lütfen yeniden giriş yapın.",
      inviteHint: "Uzverse yalnızca davetle girilir. Hesap açmak için davet bağlantınızı açın.",
    },
    invite: {
      title: "Davetinizi kabul edin",
      intro: "{{name}}, {{email}} adresini Uzverse'e davet etti.",
      displayName: "Adınız",
      password: "Bir parola seçin",
      passwordHint: "En az 12 karakter.",
      disclosure: "Bu Uzverse'ün sahibinin burada yaptığım her şeyi görebileceğini anlıyorum.",
      accept: "Hesap oluştur",
      enrollTitle: "Doğrulama uygulamanızı kurun",
      enrollHint:
        "Bu bağlantıyı bir doğrulama uygulamasıyla açın ya da anahtarı elle girin. Sonra 6 haneli kodu yazın.",
      manualKey: "Kurulum anahtarı",
      openLink: "Doğrulama uygulamasında aç",
      confirm: "Onayla ve giriş yap",
      invalid: "Bu davet geçerli değil ya da süresi dolmuş.",
    },
    chat: {
      title: "Asistanınız",
      you: "Siz",
      assistant: "Asistan",
      placeholder: "Asistanınızdan bir şey isteyin…",
      messageLabel: "Mesaj",
      send: "Gönder",
      speakReplies: "Yanıtları sesli oku",
      empty: "İhtiyacınızı söyleyin. Gerisini asistanınız halleder.",
      log: "Konuşma",
      streaming: "Yazıyor…",
      connection: {
        connecting: "Bağlanıyor…",
        online: "Bağlı",
        offline: "Çevrimdışı. Yeniden bağlanılıyor…",
      },
      artifact: "{{name}} dosyasını aç",
    },
    task: {
      state: {
        received: "Alındı",
        routing: "Doğru ekip bulunuyor",
        planning: "Planlanıyor",
        in_progress: "Sürüyor",
        review: "İnceleniyor",
        awaiting_approval: "Onayınız bekleniyor",
        done: "Tamamlandı",
        needs_owner: "Sahibinin ilgisi gerekiyor",
        cancelled: "İptal edildi",
      },
      progress: "İlerleme: {{text}}",
      untitled: "Görev",
    },
    tasks: {
      title: "Görevler",
      empty: "Henüz görev yok. Sohbette asistanınızdan isteyin.",
      active: "Süren",
      finished: "Biten",
      artifacts: "Dosyalar",
    },
    approvals: {
      title: "Onaylar",
      intro: "Asistanınız Uzverse dışında bir şey yapmadan önce size sorar.",
      empty: "Sizi bekleyen bir şey yok.",
      pending: "Sizi bekleyenler",
      resolved: "Karara bağlananlar",
      wants: "{{tool}} çalıştırmak istiyor",
      input: "Ayrıntılar",
      effect: {
        none: "Dışarıya etkisi yok",
        internal: "Uzverse içinde",
        intercompany: "Başka bir şirkete mesaj",
        external: "Dışarıya mesaj ya da paylaşım",
        spend: "Para harcar",
      },
      status: {
        pending: "Bekliyor",
        approved: "Onaylandı",
        rejected: "Reddedildi",
        expired: "Süresi doldu",
      },
    },
    avatar: {
      title: "Karakterim",
      intro: "3D dünyada sen buradasın. Görünümün cihazlar arasında seninle gelir.",
      body: "Beden",
      male: "Erkek",
      female: "Kadın",
      style: "Tarz",
      styles: { office: "Ofis çalışanı", classic: "Klasik" },
      person: "Kişi",
      look: { business: "Takım {{n}}", office: "Ofis {{n}}" },
      skin: "Ten rengi",
      hair: "Saç modeli",
      hairColor: "Saç rengi",
      outfit: "Kıyafet",
      outfitColor: "Kıyafet rengi",
      surprise: "Beni şaşırt",
      save: "Karakterimi kaydet",
      saved: "Karakter kaydedildi.",
      note: "Karakterler pakete dahil gerçekçi modellerden çizilir (ofis çalışanları Microsoft Rocketbox, MIT); başka sitelerden hiçbir şey çekilmez.",
      assistantLook: "Asistanın görünümü",
      assistantNote:
        "Asistanınızın dünyada ve kendi sayfasında nasıl göründüğü. Asistan ayarlarıyla birlikte kaydedilir.",
      portrait: "{{name}}, portre",
      hairStyle: {
        buzzed: "Kısa kesim",
        parted: "Yandan ayrık",
        long: "Uzun",
        buns: "Topuz",
        "short-f": "Kısa",
        beard: "Sakal",
      },
      outfits: {
        tee: "Tişört",
        shirt: "Gömlek",
        blazer: "Ceket",
        shorts: "Şort",
        hoodie: "Kapüşonlu",
      },
    },
    mh: {
      styleLabel: "Karakter türü",
      styleMh: "MakeHuman (önerilen)",
      styleOther: "Ofis çalışanı / klasik",
      title: "Karakterim",
      sex: "Cinsiyet",
      sexes: {
        male: "Erkek",
        female: "Kadın",
      },
      presets: "Hazır görünümler",
      undo: "Geri al",
      redo: "Yinele",
      randomAll: "Beni şaşırt",
      randomize: "Rastgele",
      reset: "Sıfırla",
      save: "Karakterimi kaydet",
      saved: "Karakter kaydedildi.",
      viewLabel: "{{name}}, 3B karakter",
      loading: "Karakter yükleniyor…",
      no3d: "3B görünüm için WebGL gerekli.",
      focus: {
        face: "Yüz",
        body: "Vücut",
      },
      tabs: {
        body: "Vücut",
        face: "Yüz",
        skin: "Ten",
        hair: "Saç",
        beard: "Sakal",
        outfit: "Kıyafet",
        accessory: "Aksesuar",
      },
      skinTone: "Ten rengi",
      eyeColor: "Göz rengi",
      brows: "Kaşlar",
      hairStyle: "Saç modeli",
      noHair: "Kel",
      hairColor: "Saç rengi",
      customColor: "Özel renk",
      beard: "Sakal",
      noBeard: "Sinekkaydı",
      beardMale: "Sakal erkek karakterler için.",
      color: "Renk",
      slots: {
        top: "Üst",
        bottom: "Alt",
        shoes: "Ayakkabı",
      },
      styles: {
        business: "İş",
        smart: "İş-rahat",
        casual: "Günlük",
        sport: "Spor",
      },
      acc: {
        glasses: "Gözlükler",
        hat: "Şapkalar",
      },
      intro:
        "3B dünyadaki sensin: vücudu ve yüzü şekillendir, teni, saçı ve kıyafetleri seç. Karakterin her cihazda seninle.",
      note: "Uzverse ile gelen MakeHuman (CC0) varlıklarından yapılır; başka sitelerden hiçbir şey indirilmez.",
      assistantNote:
        "Asistanının dünyada ve kendi sayfasında nasıl göründüğü. Asistan ayarlarıyla kaydedilir.",
      sliders: {
        height: "Boy",
        weight: "Kilo",
        muscle: "Kas",
        age: "Yaş",
        proportions: "Oranlar",
        breast: "Göğüs hacmi",
        shoulders: "Omuz genişliği",
        chest: "Göğüs çevresi",
        waist: "Bel",
        hips: "Kalça genişliği",
        belly: "Karın",
        buttocks: "Basen",
        neckLength: "Boyun uzunluğu",
        neckWidth: "Boyun kalınlığı",
        armLength: "Kol uzunluğu",
        legLength: "Bacak uzunluğu",
        headSize: "Baş boyutu",
        faceFullness: "Yüz dolgunluğu",
        faceSquare: "Yuvarlak ↔ köşeli",
        faceTaper: "Kalp ↔ armut",
        faceLength: "Elmas ↔ uzun",
        jawWidth: "Çene kemiği",
        chinWidth: "Çene genişliği",
        chinHeight: "Çene yüksekliği",
        chinProtrusion: "Çene çıkıntısı",
        cheekbones: "Elmacık kemikleri",
        cheekVolume: "Yanaklar",
        forehead: "Alın yüksekliği",
        noseSize: "Burun boyutu",
        noseWidth: "Burun genişliği",
        noseLength: "Burun uzunluğu",
        noseBridge: "Burun kemeri",
        noseTip: "Burun ucu",
        nostrils: "Burun kanatları",
        eyeSize: "Göz boyutu",
        eyeSpacing: "Göz aralığı",
        eyeTilt: "Göz eğimi",
        eyeOpening: "Göz açıklığı",
        browHeight: "Kaş yüksekliği",
        browAngle: "Kaş açısı",
        lipFullness: "Dudak dolgunluğu",
        mouthWidth: "Ağız genişliği",
        mouthHeight: "Ağız konumu",
        mouthCorners: "Ağız köşeleri",
        earSize: "Kulak boyutu",
        earProtrusion: "Kepçe kulak",
      },
      items: {
        eyes: "Gözler",
        "lashes-m": "Kirpikler",
        "lashes-f": "Kirpikler",
        "brows-natural": "Doğal",
        "brows-arched": "Kemerli",
        "brows-soft": "Yumuşak",
        "brows-thin": "İnce",
        "brows-bold": "Gür",
        "brows-straight": "Düz",
        "hair-short-crop": "Kısa kesim",
        "hair-short-side": "Yandan ayrık",
        "hair-short-messy": "Dağınık",
        "hair-pixie": "Pixie",
        "hair-afro": "Afro",
        "hair-bob": "Küt",
        "hair-bob-long": "Uzun küt",
        "hair-long": "Uzun",
        "hair-ponytail": "At kuyruğu",
        "hair-braid": "Örgü",
        "beard-stubble": "Kirli sakal",
        "beard-moustache": "Bıyık",
        "beard-full": "Tam sakal",
        "beard-long": "Uzun sakal",
        "beard-goatee": "Keçi sakal",
        "top-suit-tie": "Takım + kravat",
        "top-suit-3": "Üçlü takım",
        "top-suit-classic": "Klasik takım",
        "top-suit-double": "Kruvaze ceket",
        "top-shirt-striped": "Çizgili gömlek",
        "top-jacket-casual": "Spor ceket",
        "top-sweater": "Örgü kazak",
        "top-tee": "Tişört",
        "top-polo": "Polo",
        "top-suit-f": "Takım ceketi",
        "top-suit-f2": "Kalıplı ceket",
        "top-suit-f-double": "Kruvaze ceket",
        "top-blouse": "Bluz",
        "top-turtleneck": "Balıkçı yaka",
        "top-tee-f": "Tişört",
        "top-sport-f": "Spor üstü",
        "bottom-suit": "Takım pantolonu",
        "bottom-suit-classic": "Klasik pantolon",
        "bottom-chinos": "Kumaş pantolon",
        "bottom-jeans": "Kot",
        "bottom-cargo": "Kargo",
        "bottom-shorts": "Şort",
        "bottom-suit-f": "Takım eteği",
        "bottom-suit-f2": "Takım pantolonu",
        "bottom-skirt": "Kalem etek",
        "bottom-jeans-f": "Kot",
        "bottom-leggings": "Tayt",
        "shoes-oxford": "Klasik ayakkabı",
        "shoes-loafer": "Loafer",
        "shoes-boots": "Bot",
        "shoes-sneaker-white": "Beyaz spor ayakkabı",
        "shoes-sneaker-blue": "Koşu ayakkabısı",
        "shoes-flats": "Babet",
        "shoes-booties": "Topuklu bot",
        "glasses-frames": "Çerçeve gözlük",
        "glasses-round": "Yuvarlak gözlük",
        "glasses-square": "Köşeli gözlük",
        "hat-fedora": "Fötr şapka",
        "hat-newsboy": "Kasket",
      },
      preset: {
        executive: "Yönetici",
        creative: "Yaratıcı",
        athlete: "Sporcu",
        scholar: "Akademisyen",
        casual: "Rahat",
        mature: "Olgun",
        young: "Genç",
        bearded: "Sakallı",
        elegant: "Zarif",
      },
    },
    settings: {
      title: "Ayarlar",
      saved: "Kaydedildi.",
      language: "Dil",
      assistant: {
        title: "Asistanınız",
        name: "Asistan adı",
        appearance: "Görünüm",
        appearanceDefault: "Varsayılan",
        appearanceNote: "Daha fazla görünüm 3D dünya ile gelecek.",
        proactivity: "Ne kadar inisiyatif alsın?",
        proactivityLevel: {
          off: "Kapalı",
          low: "Düşük",
          medium: "Orta",
          high: "Yüksek",
        },
        proactivityHint: {
          off: "Yalnızca siz isteyince çalışır.",
          low: "Nadiren, yalnızca önemliyse öneride bulunur.",
          medium: "Faydalı sonraki adımları önerir.",
          high: "Rutin işlerde inisiyatif alır; dışarıya dönük her şeyden önce size sorar.",
        },
      },
      voice: {
        title: "Ses",
        select: "Ses",
        none: "Ses yok (yalnızca yazı)",
        free: "Ücretsiz",
        paid: "Ücretli",
        preview: "Sesi dinle",
        previewing: "Örnek çalıyor…",
        noVoices: "Dilinizde henüz kullanılabilir bir ses yok.",
        by: "{{provider}} sağlayıcısından",
      },
      sessions: {
        title: "Oturumlar",
        intro: "Hesabınızda oturum açık tarayıcılar ve cihazlar. Tanımadığınız oturumları kapatın.",
        current: "Bu cihaz",
        lastSeen: "Son etkinlik {{when}}",
        signedInAt: "Giriş {{when}}",
        remembered: "Kullandıkça 30 gün açık kalır",
        notRemembered: "12 saat işlem yapılmazsa kapanır",
        unknownDevice: "Bilinmeyen cihaz",
        revoke: "Oturumu kapat",
        revoked: "Oturum kapatıldı.",
        devicesTitle: "Güvenilen cihazlar",
        devicesIntro: "Bu tarayıcılar gösterilen tarihe kadar doğrulama kodu sormaz.",
        noDevices: "Güvenilen cihaz yok.",
        trustedUntil: "{{when}} tarihine kadar güvenilir",
        removeDevice: "Kaldır",
        deviceRemoved: "Cihaz kaldırıldı.",
      },
      security: {
        title: "Güvenlik",
        intro:
          "İki adımlı doğrulama hesabınızı korur. Değiştirmek için parolanız ve güncel bir kod gerekir.",
        password: "Mevcut parola",
        code: "Güncel kod",
        continue: "Devam",
        recoveryAction: "Kurtarma kodlarını yenile",
        recoveryHint: "10 yeni kod oluşturur. Eskileri çalışmaz.",
        reenrollAction: "Yeni doğrulayıcı kur",
        reenrollHint:
          "Mevcut doğrulayıcının yerine geçer ve diğer cihazlarınızdaki oturumları kapatır.",
        recoveryDone: "Yeni kurtarma kodları oluşturuldu.",
        reenrolled: "Yeni doğrulayıcınız etkin.",
        ownerNote:
          "İki adımlı doğrulamanız sunucu komut satırından (reset-owner-mfa) yönetilir ve sahip panelinde tamamlanır.",
      },
      advancedLink: "Gelişmiş ayarlar",
    },
    advanced: {
      title: "Gelişmiş ayarlar",
      intro: "Bunlar kullandığınız modüllerden gelir. Çoğu kişi için varsayılanlar yeterlidir.",
      empty: "Kullanılabilir gelişmiş ayar yok.",
      reset: "Varsayılana döndür",
      on: "Açık",
      off: "Kapalı",
      default: "Varsayılan: {{value}}",
    },
    credits: {
      title: "Katkılar",
      intro:
        "Dünyadaki 3B model ve dokular ücretsiz kütüphanelerden gelir. Onaylanan her varlığın yaratıcısı ve lisansı burada.",
      empty: "Dünyada henüz üçüncü taraf varlık kullanılmıyor.",
      unavailable: "Katkılar yüklenemedi.",
      count_one: "{{count}} varlık",
      count_other: "{{count}} varlık",
      by: "{{author}} tarafından",
      unknownAuthor: "Yazar belirtilmemiş",
      license: "Lisans",
      source: "Kaynak",
      noAttribution: "Atıf gerekmiyor.",
      licenseLink: "{{license}} lisansını oku",
      builtinTitle: "Dünyayla birlikte gelenler",
      builtinIntro:
        "Bu ücretsiz (CC0) paketler dünyayla birlikte gelir: mobilya, bitki ve yüzeyler. Atıf gerekmez; yaratıcıları yine de anıyoruz.",
    },
    memory: {
      title: "Sizin hakkınızda bildiklerim",
      intro: "Asistanınız size daha iyi yardım etmek için bunları hatırlar. Kontrol sizde.",
      empty: "Henüz bir şey yok. Birlikte çalıştıkça asistanınız öğrenir.",
      kind: { fact: "Bilgi", preference: "Tercih", routine: "Rutin" },
      edit: "Düzelt",
      editLabel: "Düzeltilmiş metin",
      saveCorrection: "Düzeltmeyi kaydet",
      delete: "Sil",
      confirmDelete: "Kalıcı olarak silinsin mi? Geri alınamaz.",
      confirmYes: "Evet, sil",
      deleted: "Silindi.",
    },
    world: {
      title: "Dünya",
    },
    voice: {
      recording: "Kayıtta",
      playing: "Konuşuyor",
      talk: "Konuşmak için basılı tutun",
      listening: "Dinliyorum… göndermek için bırakın",
      transcribing: "Yazıya dökülüyor…",
      speaking: "Konuşuyor…",
      stopSpeaking: "Konuşmayı durdur",
      unavailable: "Bu tarayıcıda ses kullanılamıyor.",
      micDenied: "Mikrofon izni reddedildi.",
      failed: "Sesli giriş başarısız oldu. Lütfen tekrar deneyin.",
      browserMode: "Tarayıcınızın konuşma hizmeti kullanılıyor",
    },
    paused: {
      title: "Duraklatıldı: yapay zekâ bağlantısı yok",
      body: "Bu şirket için bağlı bir yapay zekâ hesabı yok, bu yüzden bu işe henüz başlayamıyorum. Bir hesap bağlanır bağlanmaz kendiliğinden devam eder.",
      bodyPublicBlocked:
        "Herkese açık bir yapay zekâ bağlantısı var, ancak bu şirket yalnızca kendi bağlantılarını kullanacak şekilde ayarlı, bu yüzden bu işe henüz başlayamıyorum.",
      ownerAction: "Hesap bağla",
      ownerActionPolicy: "Bağlantı politikasını değiştir",
      ownerHint: "Sahip panelinde bu şirketin Bağlantılar sayfasını açar.",
      memberNotified: "Sahibe bildirildi.",
    },
    chatUi: {
      hello: "Ne kuralım, {{name}}?",
      helloSub:
        "Asistanınıza ne istediğinizi söyleyin. Planlar, ekiplerinize dağıtır ve size haber verir.",
      suggestTitle: "Şunları deneyin",
      suggest: {
        p1: { title: "Şirket kur", prompt: "Uzmoon şirketini kur, birinci kat yazılım olsun." },
        p2: {
          title: "Haftamı planla",
          prompt: "Haftamı planla ve önce neye bakmam gerektiğini söyle.",
        },
        p3: { title: "Açılış sayfası yap", prompt: "Kafem için bir açılış sayfası yap." },
        p4: { title: "Günlük özet", prompt: "Şirketlerimin bugün neler yaptığının özetini ver." },
      },
      steps: "Adımlar",
      stepsShow: "Adımları göster",
      stepsHide: "Adımları gizle",
      stepsLabel: "Asistanınızın yaptıkları",
      cost: "Şu ana kadarki maliyet",
      costUnknown: "henüz bildirilmedi",
      attach: "Dosya ekle",
      attachSoon: "Ekler yakında geliyor",
      hint: "Göndermek için Enter, yeni satır için Shift+Enter",
      focusHint: "Yazmak için / tuşuna basın",
      readAloudOn: "Yanıtlar sesli okunuyor",
      readAloudOff: "Yanıtlar sesli okunmuyor",
      modelUsed: "Model",
      effortUsed: "Çaba",
    },
    context: {
      title: "Bağlam",
      company: "Şirket",
      building: "Bina",
      floor: "Kat",
      none: "Henüz şirket yok. Asistanınızdan bir tane kurmasını isteyin.",
      assistant: "Asistan",
      appearance: "Görünüm",
      voice: "Ses",
      noVoice: "Ses seçilmedi",
      budget: "Bütçe",
      budgetOf: "{{limit}} içinden {{used}}",
      budgetOpen: "{{used}} harcandı, sınır yok",
      budgetNone: "Henüz bütçe verisi yok.",
      allSettings: "Tüm tercihler",
      saved: "Kaydedildi",
    },
    modelPick: {
      title: "Asistan modeli",
      intro:
        "Asistanınızın nasıl düşüneceğini seçin. Sınırları şirketinizin politikası belirler; Otomatik ise kararı asistana bırakır.",
      model: "Model",
      effort: "Düşünme çabası",
      auto: "Otomatik",
      autoHint: "Asistan her istek için uygun modeli kendisi seçer.",
      tier: { fast: "Hızlı", standard: "Standart", strong: "Güçlü" },
      tierHint: {
        fast: "En hızlı ve en ucuz. Kısa cevaplar ve basit işler.",
        standard: "Dengeli. Günlük işlerin çoğu için iyi.",
        strong: "En güçlü ve en pahalı. Kod, analiz ve zor problemler.",
      },
      effortAutoHint: "Model ne kadar düşüneceğine kendi karar verir.",
      effortLevel: {
        low: { name: "Düşük", hint: "Hızlı ve ucuz. Kısa cevaplar ve basit işler." },
        medium: { name: "Orta", hint: "Günlük işler için dengeli hız ve derinlik." },
        high: { name: "Yüksek", hint: "Daha uzun düşünür. Kod, analiz ve planlama için daha iyi." },
        xhigh: {
          name: "Çok yüksek",
          hint: "Zor problemler için ekstra derinlik. Daha yavaş, daha pahalı.",
        },
        max: { name: "Maksimum", hint: "En derin akıl yürütme. En yavaş ve en pahalı." },
      },
      locked: "Şirketinizin politikası asistanın modelini sabitliyor.",
      benchmark: "Şirketinizden ipucu",
      pillAuto: "Otomatik",
      pillLabel: "Model ve çaba",
      pillTitle: "Yalnızca bu mesaj için model ve çaba seçin",
      pillNote: "Yalnızca sonraki mesaj için geçerli, şirketinizin sınırları içinde.",
    },
    companies: {
      title: "Şirketler",
      intro: "Şirketleriniz, binaları, katları ve içlerinde çalışan ajanlar.",
      empty: "Henüz şirket yok",
      emptyHint:
        "Asistanınızdan bir tane kurmasını isteyin. Binayı oluşturur ve kat liderini işe alır.",
      setup: "Asistana sor",
      setupPrompt: "Uzmoon şirketini kur, birinci kat yazılım olsun.",
      unavailable: "Şirket görünümü henüz bağlı değil.",
      buildings_one: "{{count}} bina",
      buildings_other: "{{count}} bina",
      floors_one: "{{count}} kat",
      floors_other: "{{count}} kat",
      agents_one: "{{count}} ajan",
      agents_other: "{{count}} ajan",
      noFloors: "Henüz kat yok",
      noAgents: "Henüz ajan yok",
      lead: "Lider",
      worker: "Çalışan",
      status: { idle: "Boşta", working: "Çalışıyor", offline: "Çevrimdışı" },
      budget: "Bütçe",
      select: "Bir şirket seçin",
    },
    onboarding: {
      title: "Asistanınızla tanışın",
      intro:
        "Ona bir ad ve bir ses verin. İkisini de Ayarlar'dan istediğiniz zaman değiştirebilirsiniz.",
      step: "Adım {{n}} / {{total}}",
      nameTitle: "Adı ne olsun?",
      nameHint: "Yüksek sesle söylemekten hoşlanacağınız bir ad seçin.",
      voiceTitle: "Bir ses seçin",
      voiceHint: "Birkaçını dinleyin. Ücretsiz sesler sunucuda veya tarayıcınızda çalışır.",
      next: "Devam",
      back: "Geri",
      finish: "Sohbete başla",
      skip: "Şimdilik geç",
      noVoices: "Dilinizde henüz ses yok. Daha sonra seçebilirsiniz.",
    },
    notFound: "Bu sayfa yok.",
    connection: {
      reconnecting: "Bağlanıyor…",
      failed: "Sunucuya ulaşılamadı.",
      retry: "Yeniden dene",
    },
  },
});
